/**
 * Utility functions for date and time formatting.
 *
 * i18n : ces helpers sont appelés depuis de nombreux composants ; plutôt que
 * de threader `t`/locale partout, on lit le singleton i18n (@core/i18n) comme
 * le fait SEOHead. La langue active pilote Intl.DateTimeFormat (dates/heures
 * localisées : "Mon 15 Jun" / "7 PM" en EN, "lun. 15 juin" / "19 h" en FR) et
 * les libellés relatifs (Today/Tomorrow). Les clés vivent dans le namespace
 * `polls` (datetime_*).
 */

import { logger } from "@core/lib/logger";
import i18n from "@core/i18n";

const activeLocale = (): string => i18n.language || "fr";
const td = (key: string): string => i18n.t(`polls:${key}`);

// Formater la date au format court : "lun. 15 juin" / "Mon 15 Jun"
// Format compact pour l'affichage mobile, localisé.
export const formatDate = (dateString: string) => {
  // Protection contre les valeurs undefined/null
  if (!dateString) {
    return td("datetime_date_not_set");
  }

  // Parser la date en mode local pour éviter les décalages timezone
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day); // month - 1 car JS commence à 0

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Comparer uniquement les dates sans les heures
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const tomorrowOnly = new Date(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate(),
  );

  if (dateOnly.getTime() === todayOnly.getTime()) return td("datetime_today");
  if (dateOnly.getTime() === tomorrowOnly.getTime())
    return td("datetime_tomorrow");

  // Format compact localisé : "lun. 15 juin" / "Mon 15 Jun"
  return new Intl.DateTimeFormat(activeLocale(), {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
};

// Format date long localisé : "lundi 15 juin 2026" / "Monday, June 15, 2026"
export const formatLongDate = (input: string | Date): string => {
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return String(input);
  return new Intl.DateTimeFormat(activeLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
};

// Formate une heure (h, min) selon la locale active : "19 h" (fr) / "7 PM" (en)
export const formatHourMinute = (hour: number, minute = 0): string =>
  new Intl.DateTimeFormat(activeLocale(), {
    hour: "numeric",
    ...(minute ? { minute: "2-digit" } : {}),
  }).format(new Date(2000, 0, 1, hour, minute));

// Formater l'heure de façon simple
export const formatTime = (
  timeSlots: Array<{
    hour?: number;
    minute?: number;
    duration?: number;
    start_hour?: number;
    start_minute?: number;
    end_hour?: number;
    end_minute?: number;
    label?: string;
  }>,
) => {
  if (!timeSlots?.length) return td("datetime_all_day");
  const slot = timeSlots[0];

  // Si le format est celui de l'API (start_hour, end_hour)
  if (slot.start_hour !== undefined && slot.end_hour !== undefined) {
    const start = formatHourMinute(slot.start_hour, slot.start_minute || 0);
    const end = formatHourMinute(slot.end_hour, slot.end_minute || 0);
    return `${start} - ${end}`;
  }

  // Sinon, utiliser l'ancien format (hour, minute, duration)
  if (slot.hour === undefined) {
    logger.warn("formatTime: hour undefined in timeSlot", "vote", { slot });
    return td("datetime_time_not_set");
  }

  const start = formatHourMinute(slot.hour, slot.minute || 0);
  if (slot.duration) {
    const endTotal = slot.hour * 60 + (slot.minute || 0) + slot.duration;
    const end = formatHourMinute(Math.floor(endTotal / 60), endTotal % 60);
    return `${start} - ${end}`;
  }
  return start;
};
